import { computed, event, observable } from "@legendapp/state";
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { apiSwr, routes } from '_api';
import { toastSuccess } from '_components/ToastProvider';
import { $currentAttendanceId, $viewingAttendanceId } from '_pages/service/queue/state';
import { AuthService } from "_services/auth.service";
import { GeneratedDeskUserToken } from '_types/responses/generated-desk-user-token';
import { DeskClientOnline } from "_types/responses/Ws/desk/desk-client-online";
import { JoinDesks } from "_types/responses/Ws/desk/join-desks";
import { QueueInfo } from "_types/responses/Ws/desk/queue-info";
import { iso2jsDate } from "_utils/date";
import { valuesOf } from "_utils/obj";
import { Route } from '_utils/url';
import Config from "../../config";
import { assert } from "console";

const url = Config.API_URL + '/user-desk-hub';
const tokenStorageKey = () => `user-desk_${AuthService.currentUser!.id}`;

export const UserDeskHub = new class {
    private readonly _listenMap = new Map<string, (...args: any[]) => any>([
        ["Connected", this.onConnected.bind(this)],
        ["Update", this.Update.bind(this)],
        ["DesksJoined", this.DesksJoined.bind(this)],
        ["TokenNotFound", this.TokenNotFound.bind(this)],
        ["AttendanceFinished", this.AttendanceFinished.bind(this)],
        ["MeetGenerated", this.MeetGenerated.bind(this)],
        ["Alive", this.Alive.bind(this)],
    ]);

    private _hub: HubConnection | null = null;
    private _lastUpdate = new Date();

    private _$isConnected = observable(false);
    private _$token = observable<string | undefined>(undefined);

    public $myInfo = observable<QueueInfo | null>(null);
    public $desksJoined = observable(false);
    public $onTokenReset = event();

    public $desksInfo = observable<{ [key: string]: DeskClientOnline }>({});
    public $allDesksInfo = computed(() => {
        return valuesOf(this.$desksInfo.get()).map(v => v.clients).flat();
    });
    public connectionStarted = false;

    public get $token() {
        return this._$token;
    }

    public get $isConnected() {
        return this._$isConnected;
    }

    constructor() {
        this._$isConnected.onChange(() => {
            if (this.$token.get()) {
                this.joinDesks();
            }
        });

        setInterval(this.checkDesks.bind(this), 1000);

        setInterval(this.lastUpdateCheck.bind(this), 1000);
    }

    private lastUpdateCheck() {
        if (!this._$isConnected.get()) {
            return;
        }

        const now = new Date();

        if (now.getTime() - this._lastUpdate.getTime() > 1000 * 5) {
            this._hub?.invoke('Alive');
        }
        else if (now.getTime() - this._lastUpdate.getTime() > 1000 * 60) {
            window.location.reload();
        }
    }

    private Alive() {
        this._lastUpdate = new Date();
    }


    private checkDesks() {
        if (this._$isConnected.get() && !this.$desksJoined.$) {
            this.joinDesks();
        }
    }

    public storeGeneratedToken(value: GeneratedDeskUserToken) {

        localStorage.setItem(
            tokenStorageKey(),
            JSON.stringify(value)
        );
        this._$token.set(value.token);
    }

    public loadGeneratedToken() {
        try {
            const key = tokenStorageKey();
            const token = JSON.parse(localStorage.getItem(key) || '{}') as GeneratedDeskUserToken;

            if (token && token.token) {
                const expiration = iso2jsDate(token.expirationDate);
                if (!expiration || expiration < new Date()) {
                    localStorage.removeItem(key);
                    this._$token.set(undefined);
                    return false;
                }

                this._$token.set(token.token);
                return true;
            }
            return false;
        } catch (e) {
            console.error(e);
            return false;
        }
    }

    public async connect() {

        if (this._hub) {
            return;
        }

        this.connectionStarted = true;
        this._hub = new HubConnectionBuilder()
            .withUrl(url)
            .withAutomaticReconnect([0, 2000, 10000, 30000])
            .build();

        this._listenMap.forEach((handler, event) => {
            this._hub?.on(event, handler);

        });

        await this._hub.start();
    }

    public joinDesks() {
        if (!this.$isConnected.get()) {
            return;
        }

        this._hub?.invoke('JoinDesks', {
            token: this._$token.get(),
        } as JoinDesks);
    }

    public disconnect() {
        this.connectionStarted = false;
        this._hub?.stop();
        this._hub = null;
        this._$isConnected.set(false);
    }

    private Update(queue: DeskClientOnline) {
        this.$desksInfo.set(
            {
                ...this.$desksInfo.get(),
                [queue.deskId]: queue
            }
        );
    }

    private async MeetGenerated() {
        const currentAtt = $currentAttendanceId.get();

        if (currentAtt) {
            await apiSwr.invalidateQueries({ queryKey: [...routes.attendance(currentAtt).show] });
        }
    }

    private async AttendanceFinished({ attendanceId, conferenceId }: { attendanceId: string, conferenceId?: string }) {
        if (attendanceId && attendanceId == $currentAttendanceId.get()) {
            // $currentAttendanceId.set(undefined);
            // toastSuccess('Atendimento finalizado.');
            // setTimeout(() => Route.navigate(-1 as any), 100);
            $viewingAttendanceId.set(attendanceId);
            await apiSwr.invalidateQueries({ queryKey: [...routes.attendance(attendanceId).show] });
        }

        // await apiSwr.invalidateQueries({ queryKey: [...routes.attendances.currentSession] });
    }

    private TokenNotFound() {
        localStorage.removeItem(tokenStorageKey());
        this.$onTokenReset.fire();
    }

    private DesksJoined() {
        this.$desksJoined.set(true);
    }

    private onConnected() {
        this._$isConnected.set(true);
    }
};
